if (!customElements.get('wf-j15-3d')) {
  customElements.define('wf-j15-3d', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-open]');
      this.status = this.querySelector('[data-status]');
      this.opener.addEventListener('click', () => this.open(), options);
      this.colourButtons = [...this.querySelectorAll('[data-colour]')];
      this.colourButtons.forEach(button => button.addEventListener('click', () => this.setColour(button.dataset.colour), options));
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('[data-retry]').addEventListener('click', () => this.load(), options);
      this.dialog.addEventListener('close', () => {
        this.opener.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      }, options);
      this.hidden = false;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.loadTimer);
      if (this.dialog?.open) this.dialog.close();
    }
    open() {
      this.dialog.showModal();
      const section = this.closest('.shopify-section') || document;
      const selectedValues = [...section.querySelectorAll('variant-selects input:checked, variant-selects select option:checked')].map(input => input.value);
      const id = section.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      if (this.dataset.colourVariants) {
        const map = Object.fromEntries(this.dataset.colourVariants.split(',').map(item => item.split(':')));
        this.colour = selectedValues.find(value => Object.values(map).includes(value)) || map[id] || 'Black';
      }
      if (this.dataset.modelConfigurations) {
        const configurations = JSON.parse(this.dataset.modelConfigurations);
        const chosen = configurations.find(item => item.options.every(option => selectedValues.includes(option))) || configurations.find(item => item.id === id) || configurations[0];
        this.modelSource = chosen.battery === 'dual' ? this.dataset.dualSrc : this.dataset.src;
        this.colour = chosen.colour;
        this.querySelector('[data-configuration-label]').textContent = chosen.battery === 'dual' ? ' · 2 × 13 Ah' : ' · 1 × 13 Ah';
      }
      const changed = this.viewerSource && this.viewerSource !== (this.modelSource || this.dataset.src);
      if (this.dataset.colourVariants && this.viewer?.loaded && !this.failed && !changed) this.setColour(this.colour);
      if ((!this.viewer?.loaded || this.failed || changed) && !this.loading) this.load();
    }
    async setColour(colour) {
      const viewer = this.viewer;
      if (!viewer?.loaded || !viewer.availableVariants.includes(colour)) return;
      this.colour = colour;
      this.querySelector('wf-3d-views')?.stopTour();
      this.colourButtons.forEach(button => { button.disabled = true; button.setAttribute('aria-pressed', String(button.dataset.colour === colour)); });
      try {
        if (viewer.variantName !== colour) {
          await new Promise((resolve, reject) => {
            const timer = setTimeout(() => { viewer.removeEventListener('variant-applied', done); reject(new Error('Colour timeout')); }, 15000);
            const done = () => { clearTimeout(timer); resolve(); };
            viewer.addEventListener('variant-applied', done, {once:true});
            viewer.variantName = colour;
          });
        }
      } catch (error) { this.fail(); }
      finally { if (this.viewer === viewer && this.isConnected) this.colourButtons.forEach(button => button.disabled = this.failed); }
    }
    async loadLibrary() {
      if (customElements.get('model-viewer')) return;
      if (!window.wfJ15ViewerLoader) {
        window.wfJ15ViewerLoader = new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('Viewer timed out')), 20000);
          const script = document.createElement('script');
          script.type = 'module';
          script.src = '/cdn/shopifycloud/model-viewer/v1.12/model-viewer.js';
          script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('Viewer unavailable')); };
          customElements.whenDefined('model-viewer').then(() => { clearTimeout(timer); resolve(); });
          document.head.append(script);
        }).catch(error => { window.wfJ15ViewerLoader = null; throw error; });
      }
      return window.wfJ15ViewerLoader;
    }
    async load() {
      if (this.loading) return;
      const retrying = this.failed;
      const modelSource = this.modelSource || this.dataset.src;
      this.colourButtons.forEach(button => { button.disabled = true; });
      this.loading = true; this.failed = false;
      this.status.textContent = this.dataset.loading;
      this.querySelector('[data-retry]').hidden = true;
      this.viewer?.remove();
      this.viewer = null;
      try {
        await this.loadLibrary();
        if (!this.isConnected) return;
        const viewer = document.createElement('model-viewer');
        this.viewer = viewer;
        viewer.setAttribute('alt', this.dataset.alt || 'Wheely J15 Max — 3D');
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('touch-action', 'pan-y');
        // M20-only studio pilot: a fixed side key creates moving reflections as
        // the camera orbits. Keep lighting stable when pausing or selecting views.
        const studioM20 = this.dataset.modelLayout === 'm20';
        viewer.setAttribute('shadow-intensity', studioM20 ? '.85' : '1');
        viewer.setAttribute('exposure', studioM20 ? '1.1' : '1');
        viewer.setAttribute('environment-image', studioM20
          ? 'https://cdn.shopify.com/s/files/1/0513/1698/1950/files/engwe-m20-studio-side-v2-20261003.hdr'
          : 'https://cdn.shopify.com/s/files/1/0513/1698/1950/files/engwe-studio-v4.hdr');
        viewer.setAttribute('shadow-softness', studioM20 ? '.7' : '.55');
        viewer.setAttribute('camera-orbit', this.dataset.cameraOverview || '-25deg 78deg 95%');
        viewer.setAttribute('interaction-prompt', 'none');
        if (this.dataset.colourVariants) viewer.variantName = this.colour || 'Black';
        viewer.addEventListener('load', () => {
          if (this.viewer !== viewer) return;
          clearTimeout(this.loadTimer);
          this.loading = false; this.failed = false;
          if (modelSource !== (this.modelSource || this.dataset.src)) { this.load(); return; }
          this.status.textContent = '';
          if (this.dataset.colourVariants) this.setColour(this.colour || 'Black');
        }, { once: true });
        viewer.addEventListener('error', () => { if (this.viewer === viewer) this.fail(); }, { once: true });
        this.loadTimer = setTimeout(() => this.fail(), 60000);
        this.querySelector('[data-stage]').append(viewer);
        // model-viewer caches failed URL loads; a retry needs a fresh cache key.
        const source = new URL(modelSource, document.baseURI);
        this.viewerSource = modelSource;
        if (retrying) source.searchParams.set('wf_retry', String(Date.now()));
        viewer.src = source.href;
      } catch (error) { this.fail(); }
    }
    fail() {
      clearTimeout(this.loadTimer);
      this.loading = false; this.failed = true;
      this.status.textContent = this.dataset.error;
      this.querySelector('[data-retry]').hidden = false;
    }
  });
}
